import router from '@/router';
import { getConfiguration, getPortalInfo, getAcceptTerms, confirmAcceptTerms, logoff } from '@/services/authentication';
import { configure, validateClientID, UrlUtility, User, UserInfoService } from '@/helpers/userManager';
import { showMessage } from '@/helpers/page';

let _userManager = null

const getUserManager = function (config) {
  if (_userManager === null ||
    typeof _userManager !== 'object' ||
    Array.isArray(_userManager)
  ) {
    _userManager = configure(config)
  }
  return _userManager
}

export default {
  getConfig: async ({ commit, dispatch }) => {
    try {
      const sessionStoragePersist = JSON.parse(sessionStorage.getItem('i4pro-portal'));
      const pageIsRedirect = (window.location.href).toString().includes('redirect?');

      // Verifica se a página foi recarregada
      const [navigationEntry] = performance.getEntriesByType('navigation');
      const isReload = navigationEntry && navigationEntry.type === 'reload';

      // getConfiguration() só deve ser executada quando NÃO for especificado clientId por parâmetro.
      // durante a autenticação deve-se usar a config que está salva na memória (vuex-persist)
      let data;
      if (sessionStoragePersist && (pageIsRedirect || isReload)) {
        data = sessionStoragePersist.authentication.config;
        if (data) commit('setConfig', data);
      } else {
        data = (await getConfiguration()).data;
        if (data) commit('setConfig', data);
        await dispatch('layout/getConfigLayout', null, { root: true });
      }
    } catch (error) {
      throw Error(error);
    }
  },

  login: async ({ state }) => {
    try {
      getUserManager(state.config).signinRedirect();
    } catch (error) {
      throw Error(error);
    }
  },

  integratedLogin: async ({ state, commit }) => {
    try {
      const arrUrlParms = window.location.href.split('?');
      const errorMessage = 'Parâmetros obrigatórios não encontrados';

      if (arrUrlParms.length < 2) throw Error(errorMessage);

      const arrQueryParms = arrUrlParms[1].split('&');
      const arr = arrQueryParms.map(item => item.split('='));
      const issuer = arr.find(v => v[0] == 'Issuer');
      const externalToken = arr.find(v => v[0] == 'ExternalToken');
      const obj = {};

      if (!issuer || !externalToken) throw Error(errorMessage);

      arr.forEach(item => (obj[item[0]] = item[1]));

      await getUserManager(state.config).signinRedirect({
        extraQueryParams: obj
      });
    } catch (error) {
      showMessage(commit, { message: error, color: 'error', sweetAlert: true });
    }
  },

  redirect: async ({ state, dispatch }) => {
    try {
      await getUserManager(state.config).signinCallback(window.location.href);

      await dispatch('authentication/getProfile', null, { root: true });
    } catch (error) {
      console.error('Redirect error:', error);

      // Don't loop on callback errors - redirect to login instead
      if (error.message === 'CALLBACK_STATE_MISSING' ||
          error.message === 'CALLBACK_IN_PROGRESS' ||
          error.message === 'NOT_A_CALLBACK' ||
          error.message?.includes('state missing')) {
        console.warn('Callback issue detected, redirecting to login:', error.message);
        window.location.href = window.location.origin + window.location.pathname;
        return; // Don't throw to avoid error propagation
      } else {
        window.location.href = window.location.origin + window.location.pathname;
        throw Error(error);
      }
    }
  },

  redirectExternal: async ({ state, dispatch }) => {
    try {
      var values = UrlUtility.parseUrlFragment(window.location.href, '?');
      const errorMessage = 'Parâmetros obrigatórios não encontrados';

      if (!values.access_token)
        throw Error(errorMessage);

      if (!state.config.AUTHORITY) {
        // problemas de sincronismo no App.vue!!
        // o evento de beforeCreate chama authentication/getConfig mas ainda não terminou e
        // o VUE já chamou o evento created que por sua vez chama este método!!
        // por isso vamos chamar novamente o getConfig para ter os parâmetros necessários
        await dispatch('authentication/getConfig', null, { root: true });
      }

      const userManager = getUserManager(state.config);
      var info = new UserInfoService(userManager.settings);
      var claims = await info.getClaims(values.access_token);

      await userManager.storeUser(new User({
        access_token: values.access_token,
        token_type: 'Bearer',
        profile: claims
      }));

      await dispatch('authentication/getProfile', null, { root: true });

    } catch (error) {
      throw Error(error);
    }
  },

  getProfile: async ({ commit, state, dispatch }) => {
    try {
      const user = await getUserManager(state.config).getUser();

      if (user) {
        commit('setUserProfile', user.profile);
        commit('setToken', user.access_token);

        await dispatch('authentication/getPortalInfo', null, { root: true });
      }
    } catch (error) {
      throw Error(error);
    }
  },

  getPortalInfo: async ({ commit, dispatch, rootState }) => {
    try {
      const { data } = await getPortalInfo();

      if (data) {
        await dispatch('module/getDefaultModule', null, { root: true });
        commit('setPortalInfo', data);

        if (rootState.authentication.config.DEV_MODULE) {
          router.push({ path: '/dev' });
        } else {
          router.push({ path: '/dashboard' });
        }

        if (data.isAcceptTerms) {
          const { data: message } = await getAcceptTerms();

          commit('setAcceptTerms', {
            isAcceptTerms: data.isAcceptTerms,
            message: message.description
          });
        }
      }
    } catch (error) {
      throw Error(error);
    }
  },

  logoff: async ({ state, commit }) => {
    try {
      const { status } = await logoff(state.profile.sub);

      if (status === 200) {
        await getUserManager(state.config).signoutRedirect();
        sessionStorage.clear();
        // Removido dia: 07-03-23. O redirect é feito via oidc.
        // router.push({ path: '/login', query: queryPtlClient });
      }
    } catch (error) {
      await commit('clear');
      await commit('menu/logoff', null, { root: true });
      await commit('module/logoff', null, { root: true });
      await commit('dev/logoff', null, { root: true });

      router.push({ path: '/login' });
      throw Error(error);
    }
  },

  clear: ({ commit }) => {
    commit('clear');
  },

  confirmAcceptTerms: async ({ commit }) => {
    try {
      const data = confirmAcceptTerms();

      if (data) {
        commit('setConfirmAcceptTerms', false);
      }
    } catch (error) {
      throw Error('Ocorreu um erro de API.');
    }
  },

  changeClientId: ({ state, commit }, urlKeyToClientID) => {
    if (!urlKeyToClientID) return;

    const actualConfig = state.config;
    const newConfig = validateClientID(state.config, urlKeyToClientID);

    if (actualConfig.CLIENT_ID !== newConfig.CLIENT_ID ) {
      _.merge(actualConfig, newConfig)
      commit('setConfig', actualConfig)
    };
  }
};
